import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {MediaQueryList|undefined} */
  #mediaQuery = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Track viewport and ensure behavior only on mobile
    this.#mediaQuery = window.matchMedia('(max-width: 749px)');
    // Some browsers use addEventListener, older use addListener
    this.#mediaQuery.addEventListener?.('change', this.#handleViewportChange);
    this.#mediaQuery.addListener?.(this.#handleViewportChange);
    this.#handleViewportChange();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !this.#isMobile()) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused || !this.#isMobile()) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    // On desktop, ensure all slides are visible and no aria-hidden toggling
    if (!this.#isMobile()) {
      this.#showAllSlides();
      return;
    }

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  /** Ensure behavior only on mobile, and show all on desktop */
  #handleViewportChange = () => {
    if (this.#isMobile()) {
      // Reset and start autoplay if available
      this.pause();
      this.play();
      // Re-apply current to set aria-hidden correctly for mobile
      this.current = this.current;
    } else {
      this.pause();
      this.#showAllSlides();
    }
  };

  #isMobile() {
    return this.#mediaQuery ? this.#mediaQuery.matches : window.matchMedia('(max-width: 749px)').matches;
  }

  #showAllSlides() {
    this.refs.slides?.forEach((slide) => {
      slide.setAttribute('aria-hidden', 'false');
    });
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
